// @ts-nocheck — mixin methods are composed onto BackendService at runtime; instance API is typed via adminBackendServiceInterface.ts.
import type BaseBackendService from '../BaseBackendService';
import BackendAuthHandler from 'BackendAuthHandler';
import {
  PermissionDefinition,
  UserPermissions,
  VeraPromptProposal,
  VeraIntentPromptVersion,
  VeraGlobalChatPromptOverride,
} from '../../models/admin';
import { GlobalConfiguration } from '../../models/GlobalConfiguration';
import { encodeVeraGuardrailMessageBase64 } from '../../utils/veraGuardrailMessageWireEncoding';

const veraPromptUploadCache = new Map<
  string,
  { uploadUrl: string; gcsPath: string; expiresAt: number }
>();

export const adminBackendServiceVeraMixin = {
  async verifyConfig(data: any, signature: string, publicKeyBase64: string): Promise<boolean> {
    return this.verifySignedConfigData(data, signature, publicKeyBase64);
  },

  mapVeraGlobalChatPromptOverride(json: any): VeraGlobalChatPromptOverride {
    const data = json?.data ?? {};
    return {
      promptOverride: data?.promptOverride ?? null,
      updatedAt: data?.updatedAt,
      updatedByAdminUserId: data?.updatedByAdminUserId,
    };
  },

  async getGlobalConfiguration(): Promise<GlobalConfiguration> {
    const fetchConfig = async () => {
      const token = await new BackendAuthHandler().withAuthToken();
      const res = await this.authenticatedFetch(this.apiURL('globalConfig'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      return res.json();
    };

    const attempt = async () => {
      const { data, signature, publicKeyBase64 } = await fetchConfig();
      if (!(await this.verifyConfig(data, signature, publicKeyBase64))) {
        console.warn('signature verification failed');
      }
      return data as GlobalConfiguration;
    };

    try {
      return (await fetchConfig()).data;
    } catch {
      return await attempt();
    }
  },

  updateGlobalConfiguration(
    cfg: import('../../models/GlobalConfiguration').GlobalConfiguration,
  ): Promise<import('../../models/GlobalConfiguration').GlobalConfiguration> {
    // veraGuardrailMessage's markdown can trip Cloud Armor's WAF and get the
    // whole save rejected — send it base64-encoded instead of as raw text.
    const { veraGuardrailMessage, ...rest } = cfg;
    const payload = {
      ...rest,
      veraGuardrailMessageBase64:
        typeof veraGuardrailMessage === 'string' && veraGuardrailMessage.trim() !== ''
          ? encodeVeraGuardrailMessageBase64(veraGuardrailMessage)
          : null,
    };
    return this.authApiMutateAuthed(
      'globalConfig',
      'PUT',
      payload,
      (json) => json.data as import('../../models/GlobalConfiguration').GlobalConfiguration,
    );
  },

  getVeraSystemPrompt(): Promise<{ productionMarkdown: string; sandboxMarkdown: string }> {
    return this.authApiGetAuthed(
      'vera/global-chat/system-prompt',
      'Network error while fetching Vera system prompt',
      (json) => json?.data ?? {},
    );
  },

  getVeraGlobalChatPromptOverride(): Promise<VeraGlobalChatPromptOverride> {
    return this.authApiGetAuthed(
      'vera/global-chat/prompt-override',
      'Network error while fetching Vera prompt override',
      (json) => this.mapVeraGlobalChatPromptOverride(json),
    );
  },

  updateVeraGlobalChatPromptOverride(
    promptOverride: string | null,
  ): Promise<VeraGlobalChatPromptOverride> {
    return this.authApiMutateAuthed(
      'vera/global-chat/prompt-override',
      'PUT',
      { promptOverride },
      (json) => this.mapVeraGlobalChatPromptOverride(json),
    );
  },

  getVeraPromptUploadUrl(
    purpose: 'preview-prompt' | 'preview-grounding' | 'proposal' | 'test-draft' | 'intent-proposal',
    chatId?: string,
  ): Promise<{ uploadUrl: string; gcsPath: string }> {
    const cacheKey = `${purpose}:${chatId ?? ''}`;
    // Don't cache proposal URLs — each one is a unique temp path
    if (purpose !== 'proposal') {
      const cached = veraPromptUploadCache.get(cacheKey);
      if (cached && cached.expiresAt - Date.now() > 2 * 60 * 1000) {
        return Promise.resolve({ uploadUrl: cached.uploadUrl, gcsPath: cached.gcsPath });
      }
    }
    const params = new URLSearchParams({ purpose });
    if (chatId) params.set('chatId', chatId);
    return this.authApiGetAuthed<{ uploadUrl: string; gcsPath: string }>(
      `vera/global-chat/system-prompt/upload-url?${params.toString()}`,
      'Network error while fetching Vera prompt upload URL',
      (json) => json,
    ).then((data) => {
      veraPromptUploadCache.set(cacheKey, {
        ...data,
        expiresAt: Date.now() + 15 * 60 * 1000,
      });
      return data;
    });
  },

  uploadTextToGcs(uploadUrl: string, text: string): Promise<void> {
    return fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'text/plain' },
      body: text,
    }).then((res) => {
      if (!res.ok) throw new Error(`GCS upload failed: ${res.status}`);
    });
  },

  saveVeraPreviewPrompt(chatId: string, markdown: string): Promise<void> {
    return this.getVeraPromptUploadUrl('preview-prompt', chatId).then(({ uploadUrl }) =>
      this.uploadTextToGcs(uploadUrl, markdown),
    );
  },

  getVeraGlobalChatGroundingPrompt(): Promise<{ productionText: string }> {
    return this.authApiGetAuthed(
      'vera/global-chat/grounding-prompt',
      'Network error while fetching Vera grounding prompt',
      (json) => json?.data ?? json,
    );
  },

  saveVeraPreviewGroundingPrompt(chatId: string, text: string): Promise<void> {
    return this.getVeraPromptUploadUrl('preview-grounding', chatId).then(({ uploadUrl }) =>
      this.uploadTextToGcs(uploadUrl, text),
    );
  },

  getVeraPreviewGoogleSearch(chatId: string): Promise<{
    globalEnabled: boolean;
    sandboxEnabled: boolean;
    effectiveEnabled: boolean;
    sandboxToggleLocked: boolean;
  }> {
    return this.authApiGetAuthed(
      `vera/global-chat/system-prompt/preview-chat/${chatId}/google-search`,
      'Network error while fetching Vera preview Google search settings',
      (json) => json?.data ?? json,
    );
  },

  saveVeraPreviewGoogleSearch(chatId: string, enabled: boolean): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `vera/global-chat/system-prompt/preview-chat/${chatId}/google-search`,
      'PUT',
      { enabled },
      () => undefined,
    );
  },

  sendVeraPreviewChat(
    contactExternalId: string,
    message: string,
    chatId: string,
  ): Promise<{
    chatId: string;
    reply: string;
    history: { role: string; text: string }[];
    guardrailWarning?: { category: string; description: string; blockedReply: string };
    googleSearchGrounding?:
      | import('../../models/admin/AdminChatAssistGoogleSearchGrounding').AdminChatAssistGoogleSearchGrounding
      | null;
  }> {
    return this.authApiMutateAuthed(
      'vera/global-chat/system-prompt/preview-chat',
      'POST',
      { contactExternalId, message, chatId, clientDate: new Date().toISOString() },
      (json) => json,
    );
  },

  getVeraProposal(proposalId: string): Promise<VeraPromptProposal> {
    return this.authApiGetAuthed(
      `vera/global-chat/system-prompt/proposals/${proposalId}`,
      'Network error while fetching Vera proposal',
      (json) => json?.data,
    );
  },

  saveVeraSystemPromptSandbox(markdown: string): Promise<void> {
    return this.authApiMutateAuthed<void>(
      'vera/global-chat/system-prompt',
      'PUT',
      { markdown },
      () => undefined,
    );
  },

  submitVeraCombinedProposal(
    markdown: string,
    intentText?: string | null,
    chatId?: string | null,
    notes?: string | null,
  ): Promise<VeraPromptProposal> {
    // Upload system prompt
    const systemUpload = this.getVeraPromptUploadUrl('proposal').then(({ uploadUrl, gcsPath }) =>
      this.uploadTextToGcs(uploadUrl, markdown).then(() => gcsPath),
    );

    // Upload intent prompt only if it has content
    const intentUpload = intentText?.trim()
      ? this.getVeraPromptUploadUrl('intent-proposal').then(({ uploadUrl, gcsPath }) =>
          this.uploadTextToGcs(uploadUrl, intentText!).then(() => gcsPath),
        )
      : Promise.resolve(null);

    return Promise.all([systemUpload, intentUpload]).then(([gcsPath, intentGcsPath]) => {
      const body: {
        gcsPath: string;
        intentGcsPath?: string;
        chatId?: string;
        notes?: string;
      } = { gcsPath };
      if (intentGcsPath) body.intentGcsPath = intentGcsPath;
      if (chatId) body.chatId = chatId;
      if (notes) body.notes = notes;
      return this.authApiMutateAuthed(
        'vera/global-chat/system-prompt/proposals',
        'POST',
        body,
        (json) => json?.data,
      );
    });
  },

  getVeraIntentPrompt(): Promise<{
    productionText: string;
    sandboxText: string;
    sandboxSource: 'default' | 'custom';
    sandboxActiveVersion: VeraIntentPromptVersion | null;
  }> {
    return this.authApiGetAuthed(
      'vera/global-chat/intent-prompt',
      'Network error while fetching Vera intent prompt',
      (json) => json?.data,
    );
  },

  saveVeraIntentPrompt(text: string): Promise<VeraIntentPromptVersion> {
    return this.authApiMutateAuthed(
      'vera/global-chat/intent-prompt',
      'POST',
      { text },
      (json) => json?.data,
    );
  },

  submitVeraIntentPromptProposal(text: string, notes?: string | null): Promise<VeraPromptProposal> {
    return this.getVeraPromptUploadUrl('intent-proposal')
      .then(({ uploadUrl, gcsPath }) =>
        this.uploadTextToGcs(uploadUrl, text).then(() => ({ gcsPath })),
      )
      .then(({ gcsPath }) => {
        const body: { gcsPath: string; notes?: string } = { gcsPath };
        if (notes) body.notes = notes;
        return this.authApiMutateAuthed(
          'vera/global-chat/intent-prompt/proposals',
          'POST',
          body,
          (json) => json?.data,
        );
      });
  },
} satisfies ThisType<BaseBackendService>;
